import { useTranslation } from "react-i18next";
import ArrayValue from "./controls/ArrayValue";
import DateValue from "./controls/DateValue";
import LabeledValue from "./controls/LabeledValue";
import ListGroup, { ListGroupItem } from "./controls/ListGroup";
import ListGroupObjectItem from "./controls/ListGroupObjectItem";
import MeasureValue from "./controls/MeasureValue";
import ObjectValue from "./controls/ObjectValue";
import PointValue from "./controls/PointValue";
import TextValue from "./controls/TextValue";
import Col from "./layout/Col";
import Grid from "./layout/Grid";
import Row from "./layout/Row";
import { TipoMuestra } from "../utils/enums";
import { LineaVertidoSchema } from "../data/LineaVertisoSchema";

export function LineaVertidoList({ value, select, remove, disabled }) {
  const { t } = useTranslation();
  return (
    <ListGroup>
      {value?.map((lineaVertido, index) => {
        const isInvalid = !LineaVertidoSchema.isValidSync(lineaVertido, {
          abortEarly: false,
        });
        return (
          <ListGroupObjectItem
            key={lineaVertido.orden ?? index}
            index={index}
            indexLabel={t("lineaVertido.orden")}
            value={lineaVertido}
            className={isInvalid ? "has-error" : ""}
            editable
            onEdit={(event) => {
              event.preventDefault();
              select(index);
              document.getElementById("lineasVertido").scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
            deletable
            onDelete={(event) => {
              event.preventDefault();
              remove(index);
            }}
            disabled={disabled}
          >
            {({ value }) => (
              <Grid>
              <Row>
                <Col>
                  <TextValue
                    label={t("lineaVertido.nombre")}
                    value={value.nombre}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={4}>
                  <LabeledValue
                    label={t("lineaVertido.destino")}
                    value={value.destino}
                    labelAttr="nombre"
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <LabeledValue
                    label={t("lineaVertido.periodicidad")}
                    value={value.periodicidad}
                    labelAttr="nombre"
                  />
                </Col>
                <Col lg={6}>
                  <MeasureValue
                    label={t("lineaVertido.horasVuelco")}
                    value={value.horasVuelco}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={4}>
                  <MeasureValue
                    label={t("lineaVertido.caudalMedio")}
                    value={value.caudalMedio}
                  />
                </Col>
                <Col lg={4}>
                  <MeasureValue
                    label={t("lineaVertido.caudalPico")}
                    value={value.caudalPico}
                  />
                </Col>
                <Col lg={4}>
                  <MeasureValue
                    label={t("lineaVertido.caudalTeorico")}
                    value={value.caudalTeorico}
                  />
                </Col>
              </Row>
              {value.destino?.codigo === "SUPERFICIAL" && (
                <Row>
                  <Col>
                    <LabeledValue
                      label={t("lineaVertido.subcuenca")}
                      value={value.subcuenca}
                      labelAttr="nombre"
                    />
                  </Col>
                </Row>
              )}
              {value.destino?.codigo === "CLOACAL" && (
                <Row>
                  <Col>
                    <LabeledValue
                      label={t("lineaVertido.prestador")}
                      value={value?.prestador}
                      labelAttr="nombre"
                    />
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  <PointValue
                    label={t("lineaVertido.ubicacionCTM")}
                    value={value.ubicacionCTM}
                  />
                </Col>
              </Row>
              {value.destino?.codigo === "SUPERFICIAL" && (
                <Row>
                  <Col>
                    <PointValue
                      label={t("lineaVertido.ubicacion")}
                      value={value.ubicacion}
                    />
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  <ArrayValue
                    label={t("lineaVertido.caracterizacion")}
                    value={value?.caracterizacion}
                  >
                    {({ value: caracterizacion }) => (
                      <ListGroup>
                        {caracterizacion.map((analisis, index) => (
                          <ListGroupItem>
                            <ObjectValue key={index} value={analisis}>
                              {({ value }) => (
                                <Grid
                                  style={{
                                    paddingLeft: "8px",
                                    paddingRight: "8px",
                                    paddingTop: "4px",
                                  }}
                                >
                                  <Row>
                                    <Col lg={8}>
                                      <TextValue
                                        label={t(
                                          "analisisLaboratorio.protocolo",
                                        )}
                                        value={value.protocolo}
                                      />
                                    </Col>
                                    <Col lg={4}>
                                      <DateValue
                                        label={t("analisisLaboratorio.fecha")}
                                        value={value.fecha}
                                      />
                                    </Col>
                                  </Row>
                                  <Row>
                                    <Col lg={6}>
                                      <TextValue
                                        label={t(
                                          "analisisLaboratorio.laboratorio",
                                        )}
                                        value={value.laboratorio}
                                      />
                                    </Col>
                                    <Col lg={4}>
                                      <LabeledValue
                                        label={t(
                                          "analisisLaboratorio.tipoMuestra",
                                        )}
                                        value={value.tipoMuestra}
                                        labelAttr="nombre"
                                      />
                                    </Col>
                                  </Row>
                                  <Row>
                                    <Col lg={6}>
                                      {value?.tipoMuestra?.codigo !==
                                        TipoMuestra.PUNTUAL && (
                                        <MeasureValue
                                          label={t(
                                            "analisisLaboratorio.periodoMuestreo",
                                          )}
                                          value={value.periodoMuestreo}
                                        />
                                      )}
                                    </Col>
                                    <Col lg={6}>
                                      <MeasureValue
                                        label={t(
                                          "analisisLaboratorio.caudalMedio",
                                        )}
                                        value={value.caudalMedio}
                                      />
                                    </Col>
                                  </Row>
                                  <ArrayValue
                                    label={t("analisisLaboratorio.resultados")}
                                    value={value.resultados}
                                  >
                                    {({ value: mediciones }) => (
                                      <ListGroup>
                                        <ListGroupItem>
                                          <Grid>
                                            <Row>
                                              <Col lg={1}>#</Col>
                                              <Col lg={4}>
                                                {t("medicion.contaminante")}
                                              </Col>
                                              <Col lg={2}>
                                                {t("medicion.signo")}
                                              </Col>
                                              <Col lg={4}>
                                                {t("medicion.cantidad")}
                                              </Col>
                                            </Row>
                                          </Grid>
                                        </ListGroupItem>
                                        {mediciones.map((medicion, index) => (
                                          <ListGroupItem key={index}>
                                            <Grid>
                                              <Row>
                                                <Col lg={1}>{index + 1}</Col>
                                                <Col lg={4}>
                                                  <LabeledValue
                                                    value={
                                                      medicion.contaminante
                                                    }
                                                    labelAttr="nombre"
                                                  />
                                                </Col>
                                                <Col lg={2}>
                                                  <TextValue
                                                    value={medicion.signo}
                                                  />
                                                </Col>
                                                <Col lg={4}>
                                                  <MeasureValue
                                                    value={medicion.cantidad}
                                                  />
                                                </Col>
                                              </Row>
                                            </Grid>
                                          </ListGroupItem>
                                        ))}
                                      </ListGroup>
                                    )}
                                  </ArrayValue>
                                </Grid>
                              )}
                            </ObjectValue>
                          </ListGroupItem>
                        ))}
                      </ListGroup>
                    )}
                  </ArrayValue>
                </Col>
              </Row>
              </Grid>
            )}
          </ListGroupObjectItem>
        );
      })}
    </ListGroup>
  );
}
